import Button, { ButtonTypes } from "atoms/buttons/button";
import HiddenField from "atoms/forms/hidden-field";
import Loader from "atoms/loaders/loader";
import HubspotFieldTypes from "constants/hubspot-field-types";
import InputFormField from "molecules/form-fields/input-form-field";
import SelectFormField from "molecules/form-fields/select-form-field";
import TextAreaFormField from "molecules/form-fields/text-area-form-field";
import Form from "molecules/forms/form";
import { useHubspotForm } from "organisms/forms/use-hubspot-form";
import React from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { CollectionUtils } from "utilities/collection-utils";
import useRecaptcha from "utilities/hooks/domain/user-settings/use-recaptcha";
import { t } from "utilities/localization-utils";
export interface FieldValueMap {
    [key: string]: string;
}
interface HubspotFormProps {
    formId: string;
    onSuccess: () => void;
    onError: () => void;
    defaultValues?: { [x: string]: string };
}
const CSS_CLASS_NAME = "c-hubspot-form";
const FIELD_TYPE_TO_FIELD_COMPONENT_MAP: Record<
    HubspotFieldTypes,
    React.FC<any>
> = {
    [HubspotFieldTypes.Text]: InputFormField,
    [HubspotFieldTypes.TextArea]: TextAreaFormField,
    [HubspotFieldTypes.Select]: SelectFormField,
    [HubspotFieldTypes.Number]: InputFormField,
    [HubspotFieldTypes.PhoneNumber]: InputFormField,
};

const HubspotForm = ({
    formId,
    onError,
    onSuccess,
    defaultValues,
}: HubspotFormProps) => {
    const {
        fields,
        isFormLoading,
        onFieldChange,
        fieldValueMap,
        onSubmit,
        formErrors,
    } = useHubspotForm(formId, defaultValues);
    const { siteKey, culture, captchaComplete, handleRecaptchaChange } =
        useRecaptcha();

    const handleSubmitForm = async () => {
        const response = await onSubmit();
        response ? onSuccess() : onError();
    };
    return (
        <Form
            cssClassName={CSS_CLASS_NAME}
            onSubmit={() => false}
            dataTestId="contact-modal--hubspot-form"
            id={formId}>
            <div className={`${CSS_CLASS_NAME}__fields`}>
                {isFormLoading && <Loader />}{" "}
                {CollectionUtils.hasValues(fields) &&
                    fields?.map((field) => {
                        const InputComponent =
                            FIELD_TYPE_TO_FIELD_COMPONENT_MAP[field.fieldType];
                        const Component = field.hidden
                            ? HiddenField
                            : InputComponent;
                        const isValid = CollectionUtils.isEmpty(
                            formErrors?.[field.name]
                        );
                        const errors = formErrors?.[field.name];
                        return (
                            <Component
                                isValid={isValid}
                                errorMessages={errors}
                                {...field.toObject()}
                                dataTestId={`hubspot-form--${field.type}--${field.name}`}
                                label={field.label}
                                options={[
                                    {
                                        value: "",
                                        isPlaceholder: true,
                                        label: "",
                                    },
                                    ...(field.options ?? []),
                                ]}
                                value={fieldValueMap?.[field.name]}
                                key={field.name}
                                id={field.name}
                                onChange={(e: any) =>
                                    onFieldChange(field.name, e)
                                }
                            />
                        );
                    })}
            </div>
            <div className={`${CSS_CLASS_NAME}__recaptcha`}>
                <ReCAPTCHA
                    hl={culture}
                    sitekey={siteKey}
                    onChange={handleRecaptchaChange}
                />
            </div>
            <div className={`${CSS_CLASS_NAME}__button`}>
                <Button
                    type={ButtonTypes.Button}
                    disabled={!captchaComplete}
                    onClick={handleSubmitForm}>
                    {t("submit")}
                </Button>
            </div>
        </Form>
    );
};

export default HubspotForm;
